/**
 * The `@domain patients` endpoints (D1.1, ARCFE-1011).
 *
 * All 20 moved together, name-intact. Transport comes from the
 * `@heidi/api/client` registry rather than a module-scope client: the package
 * cannot own the app's axios instances, and the registry hands back the SAME
 * singletons the hub used, so interceptors, auth and base URLs are unchanged.
 *
 * SEVEN of these could not move until C2b (ARCFE-1011) gave their
 * request/response types a package home. Copying the types instead would have
 * produced two definitions of one wire contract — the same drift hazard as a
 * duplicated cache key, and harder to notice.
 */
import { getScribeHttpClients } from '@heidi/api/client';
import type {
  CreateOrReuseEhrPatientRequest,
  CreateOrReuseEhrPatientResponse,
  CreatePatientProfileRequest,
  CreatePatientProfileResponse,
  GetPatientProfileResponse,
  GetPatientProfilesQueryParams,
  GetPatientProfilesResponse,
  ImportPatientsResponse,
  LinkPatientSessionsResponse,
  MergePatientProfilesResponse,
  PaginatedPatientProfilesResponse,
  PatientDocumentListResponse,
  PatientDocumentWithDownloadUrlResponse,
  PatientRecapResponse,
  PatientSummaryEmailPreviewResponse,
  PatientSummarySource,
  PostPatientProfilesV21Request,
  PreparePatientSummaryDeliveryRequest,
  PreparePatientSummaryDeliveryResponse,
  SearchPatientProfilesQuery,
  SearchPatientProfilesResponse,
  SendPatientSummaryDeliveryRequest,
  SendPatientSummaryDeliveryResponse,
  SendPatientSummaryEmailRequest,
  SendPatientSummaryEmailResponse,
  UnlinkPatientSessionsResponse,
  UpdatePatientProfileRequest,
  UpdatePatientProfileResponse,
} from '../../contracts';

const patientSummarySourcePath = (source: PatientSummarySource) =>
  'documentId' in source
    ? `/patient-summary-delivery/${source.documentId}`
    : `/patient-summary-delivery/note/${source.sessionId}`;

/** @domain patients */
export const preparePatientSummaryDelivery = (
  body: PreparePatientSummaryDeliveryRequest
) => {
  return getScribeHttpClients().scribeHttpV2.post<PreparePatientSummaryDeliveryResponse>(
    '/patient-summary-delivery/prepare',
    body
  );
};

/**
 * Drives the prepare + dispatch round-trip for the chip-confirm flow.
 *
 * Always paired with a confirm dialog on the FE — never invoke this from
 * a passive surface (a render-time effect, a query refetch, etc.). The
 * backend re-validates ownership server-side and triggers
 * `SendSmsCallerWorkflow` (Temporal → Nexus → comms → Twilio), so the
 * call is intentionally a side effect with an outbound SMS at the end.
 *
 * `success=false` responses (comms unavailable, workflow timeout, Twilio
 * rejection) still resolve to 200 — inspect `data.success` and surface
 * `data.error` to the user. `data.error_reason` carries a stable
 * machine-readable code suitable for logging / branching.
 * @domain patients
 */
export const sendPatientSummaryDelivery = (body: SendPatientSummaryDeliveryRequest) => {
  return getScribeHttpClients().scribeHttpV2.post<SendPatientSummaryDeliveryResponse>(
    '/patient-summary-delivery/send',
    body
  );
};

/**
 * M0 email delivery of a patient-summary PDF (email-only launch).
 *
 * Single round-trip: the backend prepares the password-protected PDF (password
 * derived from the supplied `last_name` + `birth_date`) and emails a secure
 * download link with the document name in the subject + body. Works with or
 * without a linked patient profile. Same response semantics as
 * {@link sendPatientSummaryDelivery} — inspect `data.success`.
 * @domain patients
 */
export const sendPatientSummaryEmailDelivery = (body: SendPatientSummaryEmailRequest) => {
  return getScribeHttpClients().scribeHttpV2.post<SendPatientSummaryEmailResponse>(
    '/patient-summary-delivery/send-email',
    body
  );
};

/**
 * Fetches the rendered patient-summary PDF for the owning clinician's preview.
 *
 * Returns the UNENCRYPTED PDF (no password): the request is authenticated (the
 * `scribeHttpV2` interceptor attaches the bearer token) and the backend
 * authorizes against the source's session ownership, so the at-rest password
 * — a recipient control for the patient's emailed copy — is unnecessary here.
 * The bytes are fetched over the authenticated channel and turned into a local
 * `blob:` URL by the caller, so no public/presigned PHI URL is ever exposed.
 * @domain patients
 */
export const getPatientSummaryPreviewPdf = (source: PatientSummarySource) => {
  return getScribeHttpClients().scribeHttpV2.get<Blob>(
    `${patientSummarySourcePath(source)}/preview-pdf`,
    {
      responseType: 'blob',
    }
  );
};

/**
 * Fetches the localized patient-email preview (subject + plain-text body) for
 * the owning clinician's send modal.
 *
 * The backend renders this with the SAME copy as the actually-sent email, so
 * the clinician previews exactly what the patient receives — and the copy can
 * never drift between preview and send. The copy is name-free (PII removal) and
 * localized to the clinician's request locale. `link_placeholder` is the token
 * inside `body_text` the FE turns into the clickable preview link (no real URL
 * is minted just to preview). `documentType` mirrors what `send-email` receives
 * so the preview's document type matches the sent email.
 * @domain patients
 */
export const getPatientSummaryEmailPreview = (
  source: PatientSummarySource,
  documentType?: string
) => {
  return getScribeHttpClients().scribeHttpV2.get<PatientSummaryEmailPreviewResponse>(
    `${patientSummarySourcePath(source)}/email-preview`,
    { params: documentType ? { document_type: documentType } : undefined }
  );
};

/** @domain patients */
export const getPatientProfiles = (
  params: GetPatientProfilesQueryParams & { session_id?: string } = {}
) => {
  return getScribeHttpClients().scribeHttpV2.get<GetPatientProfilesResponse>(
    `/patient-profiles/`,
    { params }
  );
};

/** @domain patients */
export const getPatientProfileById = (patientProfileId: string) => {
  return getScribeHttpClients().scribeHttpV2.get<GetPatientProfileResponse>(
    `/patient-profiles/${patientProfileId}`
  );
};

/**
 * Server-paginated + server-searched patient profiles list (COLAB-26 / COLAB-340).
 *
 * Uses POST /v2.1 so that `q` (patient-name PHI) stays out of URL access logs.
 * All body fields are optional; omitting the body entirely returns the full
 * unfiltered roster in alphabetical `demographic_string` order.
 *
 * @domain patients
 */
export const getPatientProfilesV21 = (body: PostPatientProfilesV21Request = {}) => {
  return getScribeHttpClients().scribeHttpV2_1.post<PaginatedPatientProfilesResponse>(
    `/patient-profiles/`,
    body
  );
};

/** @domain patients */
export const createPatientProfile = (
  patientProfile: CreatePatientProfileRequest,
  sessionId?: string
) => {
  return getScribeHttpClients().scribeHttpV2.post<CreatePatientProfileResponse>(
    `/patient-profiles/`,
    patientProfile,
    sessionId ? { params: { session_id: sessionId } } : undefined
  );
};

/**
 * Create or reuse one workspace-scoped EHR-sourced patient profile.
 *
 * Reuse is by workspace + EHR patient id, not per clinician. `team_id` is
 * membership/tenant context; it is not the owner of the row.
 *
 * @domain patients
 */
export const createOrReuseEhrPatient = (body: CreateOrReuseEhrPatientRequest) => {
  return getScribeHttpClients().scribeHttpV2.post<CreateOrReuseEhrPatientResponse>(
    '/patient-profiles:createOrReuse',
    body
  );
};

/** @domain patients */
export const importPatientProfiles = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return getScribeHttpClients().scribeHttpV2.post<ImportPatientsResponse>(
    `/patient-profiles:import`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
};

/** @domain patients */
export const searchPatientProfiles = (
  params: SearchPatientProfilesQuery,
  sessionId?: string,
  signal?: AbortSignal
) => {
  return getScribeHttpClients().scribeHttpV2.post<SearchPatientProfilesResponse>(
    `/patient-profiles:search`,
    params,
    { signal, params: sessionId ? { session_id: sessionId } : undefined }
  );
};

/** @domain patients */
export const updatePatientProfile = (
  patientProfileId: string,
  patientProfile: UpdatePatientProfileRequest,
  sessionId?: string
) => {
  return getScribeHttpClients().scribeHttpV2.patch<UpdatePatientProfileResponse>(
    `/patient-profiles/${patientProfileId}`,
    patientProfile,
    sessionId ? { params: { session_id: sessionId } } : undefined
  );
};

/** @domain patients */
export const linkSessionsToPatientProfile = (
  patientProfileId: string,
  sessionIds: string[],
  sessionId?: string
) => {
  return getScribeHttpClients().scribeHttpV2.post<LinkPatientSessionsResponse>(
    `/patient-profiles/${patientProfileId}/sessions`,
    { session_ids: sessionIds },
    sessionId ? { params: { session_id: sessionId } } : undefined
  );
};

/** @domain patients */
export const unlinkSessionsToPatientProfile = (
  patientIProfileId: string,
  sessionIds: string[],
  sessionId?: string
) => {
  return getScribeHttpClients().scribeHttpV2.delete<UnlinkPatientSessionsResponse>(
    `/patient-profiles/${patientIProfileId}/sessions`,
    {
      data: { session_ids: sessionIds },
      ...(sessionId ? { params: { session_id: sessionId } } : {}),
    }
  );
};

/** @domain patients */
export const deletePatientProfile = (
  patientIProfileId: string,
  deleteLinkedSessions: boolean = true
) => {
  return getScribeHttpClients().scribeHttpV2.delete(
    `/patient-profiles/${patientIProfileId}`,
    {
      params: { delete_linked_sessions: deleteLinkedSessions },
    }
  );
};

/** @domain patients */
export const deletePatientProfiles = (
  patientProfileIds: string[],
  deleteLinkedSessions: boolean = true
) => {
  return getScribeHttpClients().scribeHttpV2.delete('/patient-profiles:batch-delete', {
    data: {
      patient_profile_ids: patientProfileIds,
      delete_linked_sessions: deleteLinkedSessions,
    },
  });
};

/** @domain patients */
export const mergePatientProfiles = (
  targetPatientProfileId: string,
  sourcePatientProfileIds: string[]
) => {
  return getScribeHttpClients().scribeHttpV2.post<MergePatientProfilesResponse>(
    '/patient-profiles:merge-profiles',
    {
      target_patient_profile_id: targetPatientProfileId,
      source_patient_profile_ids: sourcePatientProfileIds,
    }
  );
};

/** @domain patients */
export const uploadPatientDocument = ({
  patientProfileId,
  file,
  description,
  sessionId,
  onProgressUpdate,
  signal,
}: {
  patientProfileId: string;
  file: File;
  description?: string | null;
  sessionId?: string | null;
  onProgressUpdate?: (fileName: string, progress: number) => void;
  signal?: AbortSignal;
}) => {
  const formData = new FormData();
  formData.append('file', file);
  if (description) formData.append('description', description);
  if (sessionId) formData.append('session_id', sessionId);

  return getScribeHttpClients().scribeHttpV2.post(
    `/patient-profiles/${patientProfileId}/documents`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgressUpdate) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgressUpdate(file.name, percent);
        }
      },
    }
  );
};

/** @domain patients */
export const getPatientDocuments = (
  patientProfileId: string,
  page = 1,
  // 200 matches the BE cap (PATIENT_DOCUMENT_MAX_PAGE_SIZE) so we don't
  // silently truncate the list in the drawer. Full pagination UI will land
  // in a follow-up PR (FE-1b) once we have real load to design against.
  pageSize = 200
) => {
  return getScribeHttpClients().scribeHttpV2.get<PatientDocumentListResponse>(
    `/patient-profiles/${patientProfileId}/documents`,
    { params: { page, page_size: pageSize } }
  );
};

/** @domain patients */
export const getPatientRecap = (patientProfileId: string) => {
  return getScribeHttpClients().scribeHttpV2.get<PatientRecapResponse>(
    `/patient-profiles/${patientProfileId}/recap`
  );
};

/** @domain patients */
export const getPatientDocumentWithDownloadUrl = (
  patientProfileId: string,
  documentId: string
) => {
  return getScribeHttpClients().scribeHttpV2.get<PatientDocumentWithDownloadUrlResponse>(
    `/patient-profiles/${patientProfileId}/documents/${documentId}`
  );
};

/** @domain patients */
export const deletePatientDocument = (patientProfileId: string, documentId: string) => {
  return getScribeHttpClients().scribeHttpV2.delete<void>(
    `/patient-profiles/${patientProfileId}/documents/${documentId}`
  );
};
